import React, {useMemo, useState} from 'react';
import {Checkbox, Table as MuTable, Paper, TableBody, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, Toolbar, Typography} from '@mui/material';
import TableCell, {tableCellClasses} from '@mui/material/TableCell';
import {alpha, styled} from '@mui/material/styles';

const StyledTableCell = styled(TableCell)(({theme}) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14
  }
}));

const StyledTableRow = styled(TableRow)(({theme}) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0
  }
}));

const EnhancedTableToolbar = ({numSelected, actionEl}) => {
  return (
    <Toolbar
      sx={{
        pl: {sm: 2},
        pr: {xs: 1, sm: 1},
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity)
        })
      }}
    >
      {numSelected > 0 ? (
        <Typography
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} selected
        </Typography>
      ) : null}
      {actionEl}
    </Toolbar>
  );
}


// --- client-side sorting -----------------------------------------------
// A column opts in with `sortable`; every existing caller is untouched.

// Sort identity is the column NAME, not its value: two columns can read the
// same field (Balance and Runway both sit on creditsBalance) and must sort
// differently.
const sortKey = (el) => el.name

// `sortValue(row)` is for anything the cell derives — a runway computed from
// two fields sorts by the number you see, not by the field it was read from.
const cellSortValue = (el, row) => (el.sortValue ? el.sortValue(row) : row?.[el.value])

const isBlank = (v) => v === null || v === undefined || v === ''

// int64s arrive from protojson as STRINGS, so "9" must not sort above "85696".
// Compare numerically whenever both sides look numeric, text otherwise.
// Blanks sort last in BOTH directions — an empty cell is not "smallest", and
// flipping the direction should not parade the missing data to the top.
const compareValues = (a, b, dir) => {
  const ba = isBlank(a), bb = isBlank(b)
  if (ba && bb) return 0
  if (ba) return 1
  if (bb) return -1
  const na = Number(a), nb = Number(b)
  const r = Number.isFinite(na) && Number.isFinite(nb)
    ? na - nb
    : String(a).localeCompare(String(b), undefined, {numeric: true, sensitivity: 'base'})
  return r * dir
}

// The head row is black with white text, which MUI's sort label does not know
// about — without these it greys out the column you just sorted by.
const sortLabelSx = {
  color: 'inherit',
  '&:hover': {color: 'common.white'},
  '&.Mui-focusVisible': {color: 'common.white'},
  '&.Mui-active': {color: 'common.white'},
  '&.Mui-active .MuiTableSortLabel-icon': {color: 'common.white'}
}

const Table = ({
  column, data = [], enableCheckbox = false, selected = [], onSelectAllClick, handleSelect, actionEl, showPagination = false, pageOptions=[20, 50, 100], showToolbar = true,
  defaultPageSize, serialNumber = false,
  // Server-side pagination props
  serverSidePagination = false,
  totalCount = 0,
  currentPage = 0,
  onPageChange,
  onPageSizeChange,
  // Opt-in row click. Left undefined by every existing caller, so rows stay
  // inert unless a view asks for it — this component has many consumers and
  // making all rows clickable by default would change them all.
  onRowClick,
  // Open on a sort instead of the caller's order: {key: <column name>, dir}.
  // The header renders active, so the order on screen is always attributable
  // to a visible arrow rather than to something the feed did silently.
  defaultSort
}) => {
  const rowCount = data?.length || 0
  const isSelected = (id) => selected.indexOf(id) !== -1
  const [page, setPage] = useState(currentPage)
  const [pageSize, setPageSize] = useState(showPagination ? defaultPageSize ? defaultPageSize : pageOptions[0] : -1)
  // key === null means "leave the order the caller gave us" — most feeds here
  // arrive already sorted by the server, and that stays the default view
  // unless the caller passes defaultSort.
  const [sort, setSort] = useState(defaultSort?.key
    ? {key: defaultSort.key, dir: defaultSort.dir === 'desc' ? 'desc' : 'asc'}
    : {key: null, dir: 'asc'})

  // Sorting is client-side only. Under serverSidePagination this component
  // holds ONE page, so sorting it would reorder 20 rows and call it a sort of
  // 4,000 — the headers stay inert there instead of lying.
  const canSort = !serverSidePagination

  const handleSort = (el) => {
    const key = sortKey(el)
    // A numeric column can ask to open on desc, where the big rows are the
    // interesting ones.
    setSort((s) => (s.key === key
      ? {key, dir: s.dir === 'asc' ? 'desc' : 'asc'}
      : {key, dir: el.sortDefault === 'desc' ? 'desc' : 'asc'}))
    setPage(0)
  }

  const sortedData = useMemo(
    () => {
      if (!canSort || !sort.key) return data
      const col = column.find((el) => sortKey(el) === sort.key)
      if (!col) return data
      const dir = sort.dir === 'desc' ? -1 : 1
      // Copy first: sorting `data` in place would mutate the caller's state.
      return [...(data || [])].sort((a, b) => compareValues(cellSortValue(col, a), cellSortValue(col, b), dir))
    },
    [data, column, sort, canSort]
  )
  
  const handleChangePage = (e, newPage) => {
    e.stopPropagation()
    if (serverSidePagination && onPageChange) {
      onPageChange(newPage, pageSize)
    }
    setPage(newPage)
  }
  
  const handleChangeRowsPerPage = (e, newPageSize) => {
    e.stopPropagation()
    const newSize = parseInt(e.target.value, 10)
    setPageSize(newSize);
    setPage(0);
    if (serverSidePagination && onPageSizeChange) {
      onPageSizeChange(0, newSize)
    }
  }
  
  // For server-side pagination, use data as-is; for client-side, slice it
  const visibleRows = useMemo(
    () => {
      if (serverSidePagination) {
        return data; // Data already paginated from server
      }
      return showPagination ? sortedData.slice(
        page * pageSize,
        page * pageSize + pageSize,
      ) : sortedData
    },
    [page, pageSize, sortedData, showPagination, serverSidePagination]
  )

  // For serial numbers with server-side pagination
  const getSerialNumber = (index) => {
    if (serverSidePagination) {
      return (currentPage * pageSize) + index + 1
    }
    return (page * pageSize) + index + 1
  }

  return (
    <>
      {(enableCheckbox || actionEl) && showToolbar && <EnhancedTableToolbar numSelected={selected.length} actionEl={actionEl} />}
      <TableContainer component={Paper}>
        <MuTable aria-label="customized table">
          <TableHead>
            <TableRow>
              {serialNumber && <StyledTableCell align="left" sx={{width: 65}}>S. N.</StyledTableCell>}
              {enableCheckbox &&
              <StyledTableCell padding="checkbox">
                <Checkbox
                  color="primary"
                  indeterminate={selected.length > 0 && selected.length < rowCount}
                  checked={rowCount > 0 && selected.length === rowCount}
                  onChange={onSelectAllClick}
                  style={{color: 'white'}}
                  inputProps={{
                    'aria-label': 'select all desserts'
                  }}
                />
              </StyledTableCell>
              }
              {column.map((el, index) => {
                const active = canSort && el.sortable && sort.key === sortKey(el)
                return (
                  <StyledTableCell
                    align="left"
                    key={el.name}
                    style={el.sticky && index === column.length - 1 ? {
                      position: 'sticky', right: 0, backgroundColor: 'background.paper', zIndex: 3, boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)'
                    } : (el.style || {})}
                    sortDirection={active ? sort.dir : false}
                  >
                    {canSort && el.sortable ? (
                      <TableSortLabel
                        active={active}
                        direction={active ? sort.dir : 'asc'}
                        onClick={() => handleSort(el)}
                        sx={sortLabelSx}
                      >{el.name}</TableSortLabel>
                    ) : el.name}
                  </StyledTableCell>
                )
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row, index) => {
              const isItemSelected = isSelected(row.id);
              return (
                <StyledTableRow
                  key={`table-row-${index}`}
                  {...(enableCheckbox ? {role: 'checkbox', 'aria-checked': isItemSelected, selected: isItemSelected, onClick: (event) => handleSelect(row.id, row)} : {})}
                  {...(!enableCheckbox && onRowClick ? {onClick: () => onRowClick(row), style: {cursor: 'pointer'}} : {})}
                >
                  {serialNumber && <StyledTableCell align="left">{getSerialNumber(index)}</StyledTableCell>}
                  {enableCheckbox &&
                  <StyledTableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      checked={isItemSelected}
                      inputProps={{
                        'aria-labelledby': `enhanced-table-checkbox-${index}`
                      }}
                    />
                  </StyledTableCell>
                  }
                  {column.map((el, innerIndex) => <StyledTableCell
                    align={el.sticky && innerIndex === column.length - 1 ? "right" : "left"}
                    key={`${el.value}-${innerIndex}`}
                    style={el.sticky && innerIndex === column.length - 1 ? {
                      position: 'sticky', right: 0, backgroundColor: `${index % 2 === 0 ? 'rgb(245 245 245)' : 'white'}`, zIndex: 3, boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)'
                    } : (el.style || {})}
                  >
                    {el.cell ? el.cell(row[el.value], row) : row[el.value]}
                  </StyledTableCell>)}
                </StyledTableRow>
              )})}
          </TableBody>
        </MuTable>
      </TableContainer>
      {showPagination &&
      <TablePagination
        rowsPerPageOptions={pageOptions}
        component="div"
        count={serverSidePagination ? totalCount : data?.length}
        rowsPerPage={pageSize}
        page={serverSidePagination ? currentPage : page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />}
    </>
  );
}

export default Table
